import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const Loading = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsComplete(true);
            setTimeout(() => onComplete?.(), 500);
          }, 300);
          return 100;
        }
        return prev + Math.random() * 15 + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background-primary"
          data-testid="loading-screen"
        >
          {/* Animated logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="relative mb-12"
          >
            {/* Outer glow ring */}
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: { duration: 8, repeat: Infinity, ease: 'linear' },
                scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
              }}
              className="absolute inset-0 w-32 h-32 rounded-full border-2 border-brand-cyan/30"
              style={{
                boxShadow: '0 0 40px rgba(0, 229, 255, 0.3)',
              }}
            />
            
            {/* Inner spinning element */}
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              className="w-32 h-32 flex items-center justify-center"
            >
              <div className="w-20 h-20 relative">
                {/* Hexagon shape */}
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <motion.polygon
                    points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5"
                    fill="none"
                    stroke="#00E5FF"
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{
                      filter: 'drop-shadow(0 0 10px rgba(0, 229, 255, 0.8))',
                    }}
                  />
                </svg>
                
                {/* Center dot */}
                <motion.div
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-brand-cyan rounded-full"
                  style={{
                    boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
                  }}
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Brand name */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-display text-2xl md:text-3xl font-bold text-white mb-8 tracking-wider"
          >
            GEVEZ <span className="text-brand-cyan">SOFTWARE</span>
          </motion.h2>

          {/* Progress bar */}
          <div className="w-64 md:w-80">
            <div className="h-1 bg-background-secondary rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-brand-cyan to-brand-purple"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 0.2 }}
                style={{
                  boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
                }}
              />
            </div>
            <p className="mt-4 text-center text-text-secondary font-mono text-sm">
              {Math.min(Math.round(progress), 100)}%
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loading;
